import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";

import theme from "./theme";
import { allRoutes, findStepByPath, isEntryPath, journeyKeyFor, titleFor } from "./config/journeys";
import { elementFor } from "./components/journey/screens";
import "./screens";
import { setInsuranceType } from "./redux/actions";
import { useIdleLogout } from "./utilities/idleLogout";
import ResponsiveImage from "./components/elements/ResponsiveImage";

// Homepage is the one page loaded eagerly: it is what "/" renders, so
// deferring it would only add a round trip to the most common entry point.
import Homepage from "./components/pages/Homepage";

// Everything else is a separate chunk, fetched when its route is first
// visited. The agent dashboard and profile (57 KB of source between them) are
// the clearest case — a customer getting an instant quote has no account and
// can never reach either, but used to download both.
const Loginpage = lazy(() => import("./components/pages/Loginpage"));
const AgentProfile = lazy(() => import("./components/pages/AgentProfile"));
const AgentDashboard = lazy(() => import("./components/pages/AgentDashboard"));
const CreateUser = lazy(() => import("./components/pages/CreateUser"));
const ComingSoonPage = lazy(() => import("./components/pages/ComingSoonPage"));

// Pages outside every purchase funnel. Funnel routes are not listed anywhere
// in this file: they derive from src/config/journeys/* (see `allRoutes`), and
// the screen each one renders from components/journey/screens.js.
const STATIC_PAGES = [
  { path: "/", title: "SmartSure - Home", element: <Homepage /> },
  { path: "/logout", title: "SmartSure", element: <Navigate to="/" /> },
  { path: "/login", title: "SmartSure - Login", element: <Loginpage /> },
  { path: "/agent-profile", title: "SmartSure - Agent Profile", element: <AgentProfile /> },
  { path: "/agent-dashboard", title: "SmartSure - Agent Dashboard", element: <AgentDashboard /> },
  { path: "/create-user", title: "SmartSure - Create User", element: <CreateUser /> },
  { path: "/coming-soon", title: "SmartSure - Coming Soon", element: <ComingSoonPage /> },
  // Legacy deep-link: personal details is now one step with /traveller-details
  // rather than a second screen.
  { path: "/complete-details", title: "SmartSure", element: <Navigate to="/traveller-details" replace /> },
];

const STATIC_TITLES = Object.fromEntries(STATIC_PAGES.map((p) => [p.path, p.title]));
const NON_FUNNEL_PATHS = new Set(STATIC_PAGES.map((p) => p.path));

// Shown while a route's chunk is in flight. Deliberately plain: it renders on
// the page background with no layout of its own, so a fast chunk does not
// flash a box on screen and a slow one does not shift what is already painted.
const RouteFallback = () => (
  <Box
    sx={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CircularProgress sx={{ color: "#C9A777" }} />
  </Box>
);

const ScrollToTop = () => {
  const location = useLocation();
  // Block body, not an implicit return: Chromium's window.scrollTo() returns a
  // Promise (the scroll-completion API), and an arrow that returns it hands
  // React a Promise as this effect's cleanup function. On unmount React calls
  // it, a Promise is not callable, and the resulting TypeError unmounts the
  // whole tree — a blank page in Chrome/Edge/Brave. WebKit returns undefined,
  // which is why Safari was unaffected.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
};

const UpdateTab = () => {
  const location = useLocation();
  const insuranceType = useSelector((state) => state.insuranceType);
  useEffect(() => {
    document.title =
      STATIC_TITLES[location.pathname] || titleFor(location.pathname, insuranceType) || "SmartSure";
  }, [location, insuranceType]);
  return null;
};

const MaintenanceRedirect = () => {
  const [open, setOpen] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      window.location.href = "/";
    }, 5000);
    return () => clearTimeout(timer);
  }, []);
  const handleClose = () => {
    setOpen(false);
    window.location.href = "/";
  };
  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        <WarningIcon sx={{ verticalAlign: "middle", marginRight: 1 }} />
        Maintenance
      </DialogTitle>
      <DialogContent sx={{ textAlign: "center" }}>
        <ResponsiveImage
          src="/assets/images/maintenance.png"
          alt="Maintenance"
          sizes="(max-width: 600px) 45vw, 280px"
          sx={{ width: "50%", mb: 2 }}
        />
        <DialogContentText sx={{ color: "red", fontSize: "1.2rem" }}>
          This page is under maintenance. Redirecting you to SmartSure Home
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" variant="contained">
          Go to Home
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const IdleLogoutMount = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { WarningModal } = useIdleLogout({
    onIdle: async () => {
      try { dispatch({ type: "CLEAR_AUTH" }); } catch {}
      try {
        await fetch(process.env.REACT_APP_API_URL + "/logout", {
          method: "POST",
          credentials: "include",
        });
      } catch {}
      navigate("/");
    },
  });
  return <WarningModal />;
};

// Keeps the store's notion of "which product is being bought" in step with the
// URL.
//
// This used to redirect to the home page whenever `insuranceType` was empty,
// which meant a deep link to any funnel step — and, because the value was never
// persisted, a plain browser refresh mid-funnel — threw the agent out and lost
// the case. The journey configs already know which product owns every route, so
// the guard can adopt that product instead of giving up. It only redirects when
// the path belongs to no journey at all.
export const JourneyGuard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const insuranceType = useSelector((state) => state.insuranceType);

  useEffect(() => {
    const path = location.pathname;
    if (NON_FUNNEL_PATHS.has(path) || isEntryPath(path)) return;

    const match = findStepByPath(path, insuranceType);
    if (match) {
      // A deep link or a refresh: recover the product from the route rather
      // than discarding the visit.
      if (journeyKeyFor(insuranceType) !== match.journey.key) {
        dispatch(setInsuranceType(match.journey.insuranceType || match.journey.label));
      }
      return;
    }

    if (!insuranceType) navigate("/");
  }, [location.pathname, insuranceType, dispatch, navigate]);

  return null;
};

// Disable context menu and common dev shortcuts (legacy product behavior).
//
// In an effect with a cleanup, not in the render body. Called during render it
// registered a NEW pair of listeners on every re-render of <App/> and removed
// none — so the handler count grew for as long as the tab stayed open, and
// every right-click and keystroke walked a list that only got longer. Anonymous
// functions meant they could never be removed either.
const useLegacyInputGuards = () => {
  useEffect(() => {
    const blockContextMenu = (event) => event.preventDefault();
    const blockDevShortcuts = (event) => {
      if (event.ctrlKey && ["u", "i", "j", "s"].includes(event.key)) {
        event.preventDefault();
      }
    };
    document.addEventListener("contextmenu", blockContextMenu);
    document.addEventListener("keydown", blockDevShortcuts);
    return () => {
      document.removeEventListener("contextmenu", blockContextMenu);
      document.removeEventListener("keydown", blockDevShortcuts);
    };
  }, []);
};

const App = () => {
  useLegacyInputGuards();

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <IdleLogoutMount />
        <ScrollToTop />
        <UpdateTab />

        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {STATIC_PAGES.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}

          {/* ----------------------------------------------------------------
              Funnel routes — derived, not listed.

              Every journey in src/config/journeys/* contributes its landing
              page and one route per step. A step with a `form` renders the
              generic <JourneyStep/>; a step naming a `screen` renders that
              screen from components/journey/screens.js. Adding a step is a
              config entry; nothing here changes.
          ---------------------------------------------------------------- */}
          {allRoutes().map((route) => (
            <Route key={route.path} path={route.path} element={elementFor(route)} />
          ))}

          <Route path="*" element={<MaintenanceRedirect />} />
        </Routes>
        </Suspense>

        <JourneyGuard />
      </Router>
    </ThemeProvider>
  );
};

export default App;
